import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Row, Col, Panel, Button } from 'react-bootstrap';
import { fetchApi } from '../../Network/NetworkService';
import { exportAsExcel } from '../../Services/exportAsExcel';
import SimpleChart from '../Ui/SimpleChart';
import { notif } from '../Ui/Notification';

const ABTests = () => {
    const [data, setData] = useState(null);
    const [funnelData, setFunnelData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        start: '2026-01-21',
        end: new Date().toISOString().split('T')[0]
    });

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchApi(
                `/ab-tests/stats?start=${filters.start}&end=${filters.end}`
            );
            if (response.success) {
                setData(response.data);
            }
        } catch (err) {
            notif.addNotification('Erreur', `Erreur: ${err}`, 'warning');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchFunnel = useCallback(async () => {
        try {
            const response = await fetchApi(
                `/ab-tests/funnel?start=${filters.start}&end=${filters.end}`
            );
            if (response.success) {
                setFunnelData(response.data);
            }
        } catch (err) {
            console.error('Funnel fetch error:', err);
        }
    }, [filters]);

    useEffect(() => {
        fetchStats();
        fetchFunnel();
    }, [fetchStats, fetchFunnel]);

    const handleExport = () => {
        if (!data) return;

        const exportData = data.requestStats.map(stat => ({
            'Variant': stat.variant,
            'Total Requetes': stat.totalRequests,
            'Completees': stat.completedRequests,
            'Annulees': stat.canceledRequests,
            'En attente': stat.waitingRequests,
            'Taux completion (%)': stat.completionRate,
            'Taux annulation (%)': stat.cancelRate
        }));

        exportAsExcel(exportData, `ab_tests_${new Date().toISOString().split('T')[0]}`);
    };

    // Prepare daily chart data
    const dailyChartData = React.useMemo(() => {
        if (!data || !data.dailyStats.length) return null;

        const dates = [...new Set(data.dailyStats.map(d => d.date))].sort();
        const variants = [...new Set(data.dailyStats.map(d => d.variant))].sort();

        return {
            categories: dates,
            series: variants.map(variant => ({
                name: variant,
                data: dates.map(date => {
                    const item = data.dailyStats.find(d => d.date === date && d.variant === variant);
                    return item ? item.count : 0;
                })
            }))
        };
    }, [data]);

    // Prepare type breakdown chart data
    const typeChartData = React.useMemo(() => {
        if (!data || !data.typeStats.length) return null;

        const types = [...new Set(data.typeStats.map(d => d.type))];
        const variants = [...new Set(data.typeStats.map(d => d.variant))].sort();

        return {
            categories: types,
            series: variants.map(variant => ({
                name: variant,
                data: types.map(type => {
                    const item = data.typeStats.find(d => d.type === type && d.variant === variant);
                    return item ? item.count : 0;
                })
            }))
        };
    }, [data]);

    // Prepare comparison chart data
    const comparisonChartData = React.useMemo(() => {
        if (!data || !data.requestStats.length) return null;

        const variants = data.requestStats.map(s => s.variant);

        return {
            categories: ['Taux completion (%)', 'Taux annulation (%)'],
            series: variants.map(variant => {
                const stat = data.requestStats.find(s => s.variant === variant);
                return {
                    name: variant,
                    data: [stat.completionRate, stat.cancelRate]
                };
            })
        };
    }, [data]);

    // Prepare status percentage chart data
    const statusPercentageChartData = React.useMemo(() => {
        if (!funnelData || !funnelData.length) return null;

        return {
            categories: funnelData.map(f => f.variant.toUpperCase()),
            series: [
                {
                    name: 'En Attente',
                    data: funnelData.map(f => {
                        const total = f.paid || 1;
                        return Math.round((f.waiting / total) * 100 * 10) / 10;
                    })
                },
                {
                    name: 'Approuvé',
                    data: funnelData.map(f => {
                        const total = f.paid || 1;
                        return Math.round((f.approved / total) * 100 * 10) / 10;
                    })
                },
                {
                    name: 'Examiné/Terminé',
                    data: funnelData.map(f => {
                        const total = f.paid || 1;
                        return Math.round((f.completed / total) * 100 * 10) / 10;
                    })
                },
                {
                    name: 'Annulé',
                    data: funnelData.map(f => {
                        const total = f.paid || 1;
                        return Math.round((f.canceled / total) * 100 * 10) / 10;
                    })
                },
                {
                    name: 'Remboursé',
                    data: funnelData.map(f => {
                        const total = f.paid || 1;
                        return Math.round((f.refunded / total) * 100 * 10) / 10;
                    })
                }
            ]
        };
    }, [funnelData]);

    const getVariantColor = (variant) => {
        const colors = {
            'v1': '#1976d2',
            'v2': '#4caf50',
            'v3': '#ff9800',
            'v4': '#9c27b0'
        };
        return colors[variant] || '#757575';
    };

    // Prepare source chart data
    const sourceChartData = React.useMemo(() => {
        if (!data || !data.sourceStats || !data.sourceStats.length) return null;

        const sources = [...new Set(data.sourceStats.map(d => d.source))];
        const variants = [...new Set(data.sourceStats.map(d => d.variant))].sort();

        // Only show top 10 sources by total count
        const sourceTotals = sources.map(source => ({
            source,
            total: data.sourceStats
                .filter(s => s.source === source)
                .reduce((sum, s) => sum + s.count, 0)
        }));
        sourceTotals.sort((a, b) => b.total - a.total);
        const topSources = sourceTotals.slice(0, 10).map(s => s.source);

        return {
            categories: topSources,
            series: variants.map(variant => ({
                name: variant,
                data: topSources.map(source => {
                    const item = data.sourceStats.find(d => d.source === source && d.variant === variant);
                    return item ? item.count : 0;
                })
            }))
        };
    }, [data]);

    // Get source data for table
    const getSourceTableData = React.useMemo(() => {
        if (!data || !data.sourceStats || !data.sourceStats.length) return [];

        const sources = [...new Set(data.sourceStats.map(d => d.source))];
        const variants = [...new Set(data.sourceStats.map(d => d.variant))].sort();

        return sources.map(source => {
            const row = { source };
            let totalForSource = 0;

            variants.forEach(variant => {
                const item = data.sourceStats.find(d => d.source === source && d.variant === variant);
                row[variant] = item ? item.count : 0;
                row[`${variant}_completion`] = item ? item.completionRate : 0;
                totalForSource += row[variant];
            });

            row.total = totalForSource;
            return row;
        }).sort((a, b) => b.total - a.total);
    }, [data]);

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>A/B Tests Analytics</h2>
                    <p className="text-muted">
                        Analyse des performances par version de l'application (depuis le 21/01/2026)
                    </p>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={3}>
                    <div className="form-group">
                        <label>Date debut</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.start}
                            min="2026-01-21"
                            onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label>Date fin</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.end}
                            onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={6}>
                    <div className="form-group" style={{ paddingTop: '25px' }}>
                        <Button
                            bsStyle="primary"
                            onClick={() => { fetchStats(); fetchFunnel(); }}
                            disabled={loading}
                            style={{ marginRight: '10px' }}
                        >
                            {loading ? 'Chargement...' : 'Actualiser'}
                        </Button>
                        <Button bsStyle="success" onClick={handleExport} disabled={!data}>
                            Exporter Excel
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* KPI Cards */}
            {data && data.requestStats.length > 0 && (
                <Row className="mb-4">
                    {data.requestStats.map(stat => (
                        <Col md={6} lg={3} key={stat.variant}>
                            <Panel>
                                <Panel.Body style={{ textAlign: 'center' }}>
                                    <h3 style={{
                                        color: getVariantColor(stat.variant),
                                        marginBottom: '5px'
                                    }}>
                                        {stat.variant.toUpperCase()}
                                    </h3>
                                    <h2 style={{ margin: '10px 0' }}>{stat.totalRequests}</h2>
                                    <p className="text-muted" style={{ marginBottom: '10px' }}>requetes totales</p>
                                    <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                                        <div>
                                            <span style={{ color: '#4caf50', fontWeight: 'bold', fontSize: '18px' }}>
                                                {stat.completionRate}%
                                            </span>
                                            <br />
                                            <small className="text-muted">completion</small>
                                        </div>
                                        <div>
                                            <span style={{ color: '#f44336', fontWeight: 'bold', fontSize: '18px' }}>
                                                {stat.cancelRate}%
                                            </span>
                                            <br />
                                            <small className="text-muted">annulation</small>
                                        </div>
                                    </div>
                                </Panel.Body>
                            </Panel>
                        </Col>
                    ))}
                    {data.userStats.map(stat => (
                        <Col md={6} lg={3} key={`user-${stat.variant}`}>
                            <Panel>
                                <Panel.Body style={{ textAlign: 'center' }}>
                                    <h4 style={{ color: getVariantColor(stat.variant) }}>
                                        Utilisateurs {stat.variant.toUpperCase()}
                                    </h4>
                                    <h2 style={{ margin: '10px 0' }}>{stat.totalUsers}</h2>
                                    <p className="text-muted">nouveaux inscrits</p>
                                </Panel.Body>
                            </Panel>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Comparison Chart */}
            {comparisonChartData && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Comparaison des Taux</h4>
                            </Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="bar"
                                    height={300}
                                    categories={comparisonChartData.categories}
                                    series={comparisonChartData.series}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {/* Status Percentage Chart */}
            {statusPercentageChartData && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Repartition par Statut (%)</h4>
                            </Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="bar"
                                    height={350}
                                    categories={statusPercentageChartData.categories}
                                    series={statusPercentageChartData.series}
                                    options={{
                                        yAxis: {
                                            type: 'value',
                                            max: 100,
                                            axisLabel: {
                                                formatter: '{value}%'
                                            }
                                        },
                                        tooltip: {
                                            trigger: 'axis',
                                            axisPointer: { type: 'shadow' },
                                            formatter: function(params) {
                                                let result = params[0].axisValue + '<br/>';
                                                params.forEach(function(item) {
                                                    result += item.marker + ' ' + item.seriesName + ': ' + item.value + '%<br/>';
                                                });
                                                return result;
                                            }
                                        }
                                    }}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {/* Daily Evolution Chart */}
            {dailyChartData && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Evolution Journaliere des Requetes</h4>
                            </Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="line"
                                    height={350}
                                    categories={dailyChartData.categories}
                                    series={dailyChartData.series}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {/* Type Breakdown Chart */}
            {typeChartData && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Repartition par Type de Consultation</h4>
                            </Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="bar"
                                    height={300}
                                    categories={typeChartData.categories}
                                    series={typeChartData.series}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {/* Source Breakdown Chart */}
            {sourceChartData && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Repartition par Source (Top 10)</h4>
                            </Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="bar"
                                    height={350}
                                    categories={sourceChartData.categories}
                                    series={sourceChartData.series}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {/* Source Stats Table */}
            {data && getSourceTableData.length > 0 && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Statistiques par Source</h4>
                            </Panel.Heading>
                            <Panel.Body style={{ overflowX: 'auto' }}>
                                <table className="table table-striped table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th rowSpan={2}>Source</th>
                                            <th rowSpan={2}>Total</th>
                                            {data.requestStats.map(stat => (
                                                <th key={stat.variant} colSpan={2} style={{ textAlign: 'center', color: getVariantColor(stat.variant) }}>
                                                    {stat.variant.toUpperCase()}
                                                </th>
                                            ))}
                                        </tr>
                                        <tr>
                                            {data.requestStats.map(stat => (
                                                <React.Fragment key={`header-${stat.variant}`}>
                                                    <th>Nombre</th>
                                                    <th>Taux Complétion</th>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getSourceTableData.map(row => (
                                            <tr key={row.source}>
                                                <td><strong>{row.source}</strong></td>
                                                <td>{row.total}</td>
                                                {data.requestStats.map(stat => (
                                                    <React.Fragment key={`cell-${stat.variant}`}>
                                                        <td style={{ color: getVariantColor(stat.variant) }}>
                                                            {row[stat.variant] || 0}
                                                        </td>
                                                        <td>
                                                            <span className={row[`${stat.variant}_completion`] >= 50 ? 'label label-success' : 'label label-default'}>
                                                                {row[`${stat.variant}_completion`] || 0}%
                                                            </span>
                                                        </td>
                                                    </React.Fragment>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {/* Funnel Comparison */}
            {funnelData && funnelData.length > 0 && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Funnel de Conversion par Variant</h4>
                            </Panel.Heading>
                            <Panel.Body>
                                <table className="table table-striped table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Variant</th>
                                            <th>Total</th>
                                            <th>Incomplete</th>
                                            <th>Payé</th>
                                            <th>En Attente</th>
                                            <th>Approuvé</th>
                                            <th>Examiné/Terminé</th>
                                            <th>Annulé</th>
                                            <th>Remboursé</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {funnelData.map(row => {
                                            const paidTotal = row.paid || 1;
                                            const pct = (val) => Math.round((val / paidTotal) * 100 * 10) / 10;
                                            return (
                                                <tr key={row.variant}>
                                                    <td>
                                                        <strong style={{ color: getVariantColor(row.variant) }}>
                                                            {row.variant.toUpperCase()}
                                                        </strong>
                                                    </td>
                                                    <td>{row.total}</td>
                                                    <td>{row.incomplete}</td>
                                                    <td style={{ color: '#4caf50', fontWeight: 'bold' }}>{row.paid}</td>
                                                    <td>
                                                        {row.waiting}
                                                        <br />
                                                        <small className="text-muted">({pct(row.waiting)}%)</small>
                                                    </td>
                                                    <td>
                                                        {row.approved}
                                                        <br />
                                                        <small className="text-muted">({pct(row.approved)}%)</small>
                                                    </td>
                                                    <td style={{ color: '#1976d2', fontWeight: 'bold' }}>
                                                        {row.completed}
                                                        <br />
                                                        <small>({pct(row.completed)}%)</small>
                                                    </td>
                                                    <td style={{ color: '#ff9800' }}>
                                                        {row.canceled}
                                                        <br />
                                                        <small>({pct(row.canceled)}%)</small>
                                                    </td>
                                                    <td style={{ color: '#f44336' }}>
                                                        {row.refunded}
                                                        <br />
                                                        <small>({pct(row.refunded)}%)</small>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {/* Detailed Stats Table */}
            {data && data.requestStats.length > 0 && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Statistiques Detaillees</h4>
                            </Panel.Heading>
                            <Panel.Body>
                                <table className="table table-striped table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th>Variant</th>
                                            <th>Total Requêtes</th>
                                            <th>Examiné/Terminé</th>
                                            <th>En Attente</th>
                                            <th>Approuvé</th>
                                            <th>Annulé</th>
                                            <th>Taux Complétion</th>
                                            <th>Taux Annulation</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.requestStats.map(stat => (
                                            <tr key={stat.variant}>
                                                <td>
                                                    <strong style={{ color: getVariantColor(stat.variant) }}>
                                                        {stat.variant.toUpperCase()}
                                                    </strong>
                                                </td>
                                                <td>{stat.totalRequests}</td>
                                                <td>{stat.completedRequests}</td>
                                                <td>{stat.waitingRequests}</td>
                                                <td>{stat.approvedRequests}</td>
                                                <td>{stat.canceledRequests}</td>
                                                <td>
                                                    <span className="label label-success">
                                                        {stat.completionRate}%
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="label label-warning">
                                                        {stat.cancelRate}%
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {loading && (
                <Row>
                    <Col xs={12} style={{ textAlign: 'center', padding: '50px' }}>
                        <p>Chargement des donnees...</p>
                    </Col>
                </Row>
            )}

            {!loading && (!data || data.requestStats.length === 0) && (
                <Row>
                    <Col xs={12}>
                        <Panel>
                            <Panel.Body style={{ textAlign: 'center', padding: '50px' }}>
                                <p className="text-muted">Aucune donnee disponible pour cette periode</p>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}
        </Grid>
    );
};

export default ABTests;
